import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  PatientBenefits,
  PayerConfig,
} from "pages/patient-chart/patient-benefits/models";
import { EligibilityResponse } from "pages/patient-chart/patient-benefits/patient-eligibility/eligibility";
import {
  MediCalEligibilityResponsesResult,
} from "pages/patient-chart/patient-benefits/patient-eligibility/medi-cal-eligibility";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import { catchError, map, tap } from "rxjs/operators";
import { ParsedOutput } from "../../pages/billing/models";

/**
 * CO-2315 item #5 (PRD 1 REQ-012a) — one row of the Medi-Cal Aid Code reference
 * table, as GET /reference/aid-codes serves it.
 *
 * The table is GLOBAL, not tenant-scoped: Curantis maintains it centrally and it is
 * not customer-editable, so every agency sees the same rows.
 */
export interface AidCodeReferenceEntry {
  code: string;
  description: string;
  scope: string;
  deliveryModel: string;
}

/**
 * CO-2315 item #5 (D-4) — one recorded change to the Aid Code reference table.
 *
 * `before` is absent on a CREATE and `after` on a DELETE, which is how the two are
 * told apart without trusting `action` alone.
 */
export interface AidCodeAuditEntry {
  action: string;
  code: string;
  changedAt: string;
  changedByUserId: string;
  changedByUsername: string;
  tenantId: number;
  correlationId: string;
  before?: AidCodeReferenceEntry;
  after?: AidCodeReferenceEntry;
}

@Injectable()
export class PatientBenefitsService {
  private readonly patientBenefitUrl: string;

  /**
   * CO-2315 item #5 (F9.S4/C2): the reference table, held for the life of the
   * session. It is small, global, and changes only when Curantis Implementation
   * edits it, so one fetch per session is the right shape — the Benefits form
   * looks a code up on every keystroke and must not go to the network for it.
   *
   * AN EMPTY RESULT IS NEVER CACHED, which mirrors CachedAidCodeTable on the
   * server and is there for the same reason: an empty table makes every Aid Code
   * read as unrecognized, so caching one transient empty read would pin this
   * session to "no code has a description" until the user reloads the app.
   */
  private aidCodeCache: AidCodeReferenceEntry[] = null;

  constructor(public http: HttpClient, private config: ConfigurationService) {
    if (this.config.getConfig().isLocal) {
      this.patientBenefitUrl =
        config.getConfig().localEndpoints.patientBenefits;
    } else {
      this.patientBenefitUrl = config.getConfig().apiUrl;
    }
  }

  getPayerNames(): Observable<PayerConfig[]> {
    return this.http.post<PayerConfig[]>(
      `${this.patientBenefitUrl}/patient-benefits/config`,
      null
    );
  }

  getPatientBenefits(patientChartId: number): Observable<PatientBenefits[]> {
    return this.http.get<PatientBenefits[]>(
      `${this.patientBenefitUrl}/patient-benefits/patientChart/${patientChartId}/benefits`
    );
  }

  postPatientBenefit(patientChartId, patientBenefit: any) {
    return this.http.post(
      `${this.patientBenefitUrl}/patient-benefits/patientChart/${patientChartId}`,
      patientBenefit
    );
  }

  postInstantEligibility(body: any) {
    return this.http.post(
      `${this.patientBenefitUrl}/claim-submission/instant-eligibility`,
      body
    );
  }

  getInstantEligibility(patientChartId) {
    let url: string = `${this.patientBenefitUrl}/claim-submission/instant-eligibility/patientChartId/${patientChartId}`;
    return this.http.get<EligibilityResponse[]>(url);
  }

  /**
   * CO-3399 (A.5.3 / A.5.4): the stored Medi-Cal 271 responses for one patient chart, newest
   * first, each with the differences it has from the benefit record for that response's own
   * service month.
   *
   * A SEPARATE STORE from getInstantEligibility above, and deliberately so. That one reads
   * the claim-submission instant-eligibility records; these are written by
   * POST /patient-benefits/medi-cal-eligibility and live in the patient-benefits table. They
   * are different runs, not two views of the same one, so they are fetched separately rather
   * than merged server-side.
   */
  getMediCalEligibilityResponses(patientChartId) {
    let url: string = `${this.patientBenefitUrl}/patient-benefits/patientChart/${patientChartId}/eligibility-responses`;
    return this.http.get<MediCalEligibilityResponsesResult>(url);
  }

  getInstantEligibilityReport(
    patientChartId,
    payerId,
    benefitType,
    dateRan,
    isFromBatch
  ) {
    let url: string = `${this.patientBenefitUrl}/claim-submission/instant-eligibility/report/patientChartId/${patientChartId}/payerId/${payerId}/benefitType/${benefitType}/dateRan/${dateRan}?isFromBatch=${isFromBatch}`;
    return this.http.get<ParsedOutput>(url);
  }

  getPatientBenefitFromId(
    patientChartId,
    benefitId
  ): Observable<PatientBenefits> {
    return this.http.get<PatientBenefits>(
      `${this.patientBenefitUrl}/patient-benefits/patientChart/${patientChartId}/benefits/${benefitId}`
    );
  }

  getPayers(): Observable<PayerConfig[]> {
    return this.http.get<PayerConfig[]>(
      `${this.patientBenefitUrl}/payer-functions/payerlist`
    );
  }

  getPatientBenefitsCountByPayerId(payerId: number): Observable<number> {
    return this.http.get<number>(
      `${this.patientBenefitUrl}/patient-benefits/config/${payerId}/benefits`
    );
  }

  /**
   * CO-2315 item #5 (F9.S4): the maintained Aid Code reference table.
   *
   * Fails SOFT. A biller must still be able to record an Aid Code when the
   * reference read fails — the description beside the field is an aid, not a
   * gate — so an error resolves to an empty list rather than propagating. The
   * empty list is not cached, so the next component to ask retries.
   */
  getAidCodes(): Observable<AidCodeReferenceEntry[]> {
    if (this.aidCodeCache) {
      return of(this.aidCodeCache);
    }
    return this.http
      .get<AidCodeReferenceEntry[]>(
        // `/patient-benefits` is the API Gateway BASE PATH MAPPING, not decoration.
        // `patientBenefitUrl` is the shared domain (api.<env>.curantissolutions.com) and
        // every method here prefixes the API it is calling - which is why the neighbours
        // read `/patient-benefits/...`, `/payer-functions/...`, `/claim-submission/...`.
        // Omitting it does not 404 in an obvious way: the request misses the mapping
        // entirely and the gateway answers 403 with a SigV4 complaint about the
        // Authorization header, which reads like an auth bug. Measured in dev,
        // 2026-09-04.
        `${this.patientBenefitUrl}/patient-benefits/reference/aid-codes`
      )
      .pipe(
        map((entries) => entries || []),
        tap((entries) => {
          if (entries.length > 0) {
            this.aidCodeCache = entries;
          }
        }),
        catchError(() => of([] as AidCodeReferenceEntry[]))
      );
  }

  /**
   * CO-2315 item #5: the reference table, read fresh and the session cache updated.
   *
   * The maintenance screen must NOT be served the cached copy - it is the thing
   * that changes the table, so a cached read would show a biller's-eye view that is
   * one edit behind every save the editor just made.
   *
   * An empty result is returned as-is but NOT cached: deleting the last row is a
   * legitimate state this screen must show, while an empty biller-facing cache
   * would report every Aid Code as having no description.
   */
  refreshAidCodes(): Observable<AidCodeReferenceEntry[]> {
    return this.http
      .get<AidCodeReferenceEntry[]>(
        // `?fresh=true` bypasses the SERVER's container cache as well as this one.
        // Both exist, and skipping only the near side is the defect this call was
        // written to avoid: measured in dev 2026-09-04, a save returned 200 and the
        // read immediately after it came back without the new row, because the
        // reading Lambda held a table loaded up to fifteen minutes earlier.
        `${this.patientBenefitUrl}/patient-benefits/reference/aid-codes?fresh=true`
      )
      .pipe(
        map((entries) => entries || []),
        tap((entries) => {
          this.aidCodeCache = entries.length > 0 ? entries : null;
        })
      );
  }

  /**
   * CO-2315 item #5: add or correct one reference row. The code is the identity, so
   * an edit is a post at the same key and this one call serves both.
   *
   * Errors are NOT swallowed here. The API answers a bad row with every problem it
   * found, and the maintenance screen renders them against the fields they name; a
   * catchError in this service would turn that into a silent failure.
   */
  saveAidCode(entry: AidCodeReferenceEntry): Observable<any> {
    return this.http.post(
      `${this.patientBenefitUrl}/patient-benefits/reference/aid-codes`,
      entry
    );
  }

  /** CO-2315 item #5: remove one reference row. Global and not undoable from the
   *  screen - the audit row is what makes it recoverable. */
  deleteAidCode(code: string): Observable<any> {
    return this.http.delete(
      `${
        this.patientBenefitUrl
      }/patient-benefits/reference/aid-codes/${encodeURIComponent(code)}`
    );
  }

  /** CO-2315 item #5 (D-4): who changed which code, when, and to what. Gated
   *  server-side on the same permission as an edit. */
  getAidCodeAudit(limit?: number): Observable<AidCodeAuditEntry[]> {
    const query = limit ? `?limit=${limit}` : "";
    return this.http.get<AidCodeAuditEntry[]>(
      `${this.patientBenefitUrl}/patient-benefits/reference/aid-codes/audit${query}`
    );
  }

  canDeletePayer(payerId: number): Observable<boolean> {
    return this.getPatientBenefitsCountByPayerId(payerId).pipe(
      map((count: number) => {
        return count === 0;
      }),
      catchError((error) => {
        return of(false);
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/patient-benefits.service.ts